<template>
  <div class="event-participations">
    <Progression v-if="fetching"></Progression>
    <div v-else class="participations-contents">
      <h3 v-if="!editable" style="color:#ed1c24;">
        {{ $t("scheduler.participant.cancelled") }}
      </h3>
      <v-layout class="participants-header">
        <v-spacer></v-spacer>
        <UsersSelectionButton
          v-if="editable"
          icon="add"
          color="success lighten-1"
          :except="emails"
          @confirmed="addParticipants"
        ></UsersSelectionButton>
      </v-layout>

      <div v-if="editable" class="history hidden-md-and-up text-center">
        <v-btn
          v-if="canEdit()"
          class="white--text"
          :disabled="!undo.length"
          color="blue"
          @click="undoAction"
        >
          <v-icon>undo</v-icon>
        </v-btn>
        <v-btn
          v-if="canEdit()"
          class="white--text"
          :disabled="!redo.length"
          color="blue"
          @click="redoAction"
        >
          <v-icon>redo</v-icon>
        </v-btn>
      </div>

      <div class="flex-table">
        <div>{{ $t("user.fields.name") }}</div>
        <div>{{ $t("user.fields.role") }}</div>
        <div>{{ $t("user.fields.email") }}</div>
      </div>
      <div v-for="item in data" :key="item.id" class="participants-items">
        <Participant :id="item.id" :editable="editable" />
      </div>
      <div class="action-buttons">
        <div class="text-left action-button" style="float: left;">
          <v-btn outlined @click="back">
            {{ $t("app.buttons.back") }}
          </v-btn>
        </div>

        <div v-if="editable" class="hidden-sm-and-down action-button">
          <v-btn
            v-if="canEdit()"
            class="white--text"
            :disabled="!undo.length"
            color="blue"
            @click="undoAction"
          >
            <v-icon>undo</v-icon>
          </v-btn>
          <v-btn
            v-if="canEdit()"
            class="white--text"
            :disabled="!redo.length"
            color="blue"
            @click="redoAction"
          >
            <v-icon>redo</v-icon>
          </v-btn>
        </div>

        <div v-if="editable" class="action-button" style="float: right;">
          <ConfirmButton
            v-if="canEdit()"
            :button-text="$t('app.buttons.submit')"
            button-color="primary"
            :message-title="
              $t('scheduler.event.participations.messages.submit.title')
            "
            :message-body="
              $t('scheduler.event.participations.messages.submit.body')
            "
            :disabled="!referenceDistance"
            :loading="validating || sending"
            @confirm="send()"
          ></ConfirmButton>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from "vuex";
import Progression from "@/components/Progression";
import Participant from "./Participant";
import UsersSelectionButton from "@/modules/user/components/UsersSelectionButton";
import ConfirmButton from "@/components/utilities/ConfirmButton";
import adaptersIn from "@/services/data-adapter/incoming/scheduler";
import adaptersOut from "@/services/data-adapter/outgoing/scheduler";

export default {
  components: {
    Progression,
    Participant,
    UsersSelectionButton,
    ConfirmButton
  },

  props: {
    editable: {
      type: Boolean
    }
  },

  data() {
    return {};
  },

  computed: {
    ...mapState("toolbox/advanced-forms", [
      "fetching",
      "sending",
      "validating",
      "valid",
      "data",
      "referenceDistance",
      "undo",
      "redo"
    ]),
    emails: function() {
      const users = this.data;
      if (!users) {
        return null;
      }
      var emails = [];
      users.forEach(function(user) {
        emails.push(user.email);
      });
      return emails;
    }
  },

  watch: {
    validating: function(val) {
      // Validation ended and the result is valid
      if (!val && this.valid) {
        // Post the data
        this.$store.dispatch("toolbox/advanced-forms/send");
      }
    }
  },

  mounted: function() {
    this.$nextTick(async function() {
      // Initialize the Advanced Forms tool
      var module = "toolbox/advanced-forms/";
      var endpoint =
        "/events/" + this.$route.params.eventId + "/participations";
      this.$store.dispatch(module + "setFetchEndpoint", endpoint);
      this.$store.dispatch(module + "setSendEndpoint", endpoint);
      this.$store.dispatch(module + "setSendMethod", "put");
      this.$store.dispatch(module + "setAdapterIn", item =>
        adaptersIn.participant(item)
      );
      this.$store.dispatch(module + "setAdapterOut", function(item) {
        return adaptersOut.participant(item);
      });
      this.$store.dispatch(module + "setAdapterOutHeader", "participants");
      this.$store.dispatch(module + "fetch");
    });
  },

  beforeDestroy: function() {
    this.$store.dispatch("toolbox/advanced-forms/clear");
  },

  methods: {
    addParticipants(participants) {
      var participations = [];
      participants.forEach(function(participant) {
        participations.push({
          firstname: participant.firstname,
          lastname: participant.lastname,
          email: participant.email,
          role: participant.role,
          reason: null,
          notice: null
        });
      });
      this.$store.dispatch("toolbox/advanced-forms/pushMany", participations);
    },

    canEdit() {
      const role = this.$store.getters["site/user"].role;
      return role === "admin" || role === "teacher";
    },

    back() {
      this.$router.go(-1);
    },
    undoAction() {
      this.$store.dispatch("toolbox/advanced-forms/undoState");
    },
    redoAction() {
      this.$store.dispatch("toolbox/advanced-forms/redoState");
    },
    send() {
      this.$store.dispatch("toolbox/advanced-forms/validate");
    }
  }
};
</script>

<style lang="scss" scoped>
.flex-table {
  display: grid;
  grid-template-columns: repeat(auto-fill, 33%);
  padding: 10px;
  border-bottom: 1px black solid;
}
.participants-items {
  &:nth-of-type(2n) {
    background-color: #dedede;
  }
}
.action-buttons {
  width: 100%;
  background-color: #fafafa;
  position: -webkit-sticky;
  position: sticky;
  bottom: 0px;
  z-index: 2;
  text-align: center;
  overflow: auto;
}
.action-button {
  display: inline-block;
}
.history {
  width: 100%;
  background-color: #fafafa;
  position: -webkit-sticky;
  position: sticky;
  text-align: center;
  top: 56px;
  z-index: 2;
}
</style>
